import { useState, useEffect, useCallback } from 'react';
import { Plus, X, Loader, AlertCircle, ExternalLink, Send, Ban, Trash2, Check } from 'lucide-react';
import Header from '../components/layout/Header';
import Panel from '../components/common/Panel';
import Badge from '../components/common/Badge';
import {
  getInvoices,
  getInvoiceTemplates,
  createInvoice,
  sendInvoice,
  voidInvoice,
  getBillingPlans,
  createCheckoutSession,
  getUsage,
} from '../services/api';
import type { Invoice, InvoiceTemplate, InvoiceLineItem } from '../types';

// ── Helpers ─────────────────────────────────────────────────────────

function formatAmount(cents: number, currency = 'usd'): string {
  const dollars = (cents / 100).toFixed(2);
  const symbol = currency.toLowerCase() === 'usd' ? '$' : currency.toUpperCase() + ' ';
  return `${symbol}${dollars}`;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const statusConfig: Record<Invoice['status'], { label: string; variant: string }> = {
  draft: { label: 'Draft', variant: 'default' },
  open: { label: 'Open', variant: 'info' },
  paid: { label: 'Paid', variant: 'success' },
  void: { label: 'Void', variant: 'error' },
  uncollectible: { label: 'Uncollectible', variant: 'error' },
};

// ── Empty line item ──────────────────────────────────────────────────

function emptyLineItem(): InvoiceLineItem {
  return { description: '', quantity: 1, unit_amount: 0, currency: 'usd' };
}

// ── Line item row component ──────────────────────────────────────────

interface LineItemRowProps {
  item: InvoiceLineItem;
  index: number;
  onChange: (index: number, field: keyof InvoiceLineItem, value: string | number) => void;
  onRemove: (index: number) => void;
  showRemove: boolean;
}

function LineItemRow({ item, index, onChange, onRemove, showRemove }: LineItemRowProps) {
  const [rateInput, setRateInput] = useState((item.unit_amount / 100).toFixed(2));

  const handleRateBlur = () => {
    const dollars = parseFloat(rateInput.replace(/[^0-9.]/g, ''));
    const cents = isNaN(dollars) ? 0 : Math.round(dollars * 100);
    onChange(index, 'unit_amount', cents);
    setRateInput((cents / 100).toFixed(2));
  };

  const lineTotal = (item.quantity * item.unit_amount) / 100;

  return (
    <div className="grid gap-2 items-center" style={{ gridTemplateColumns: '1fr 70px 110px 90px 32px' }}>
      {/* Description */}
      <input
        type="text"
        value={item.description}
        onChange={(e) => onChange(index, 'description', e.target.value)}
        placeholder="Description"
        className="px-3 py-2 rounded-lg text-sm outline-none"
        style={{
          background: 'var(--bg-input)',
          border: '1px solid var(--border-primary)',
          color: 'var(--text-primary)',
        }}
        onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--netrun-green)')}
        onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border-primary)')}
      />

      {/* Quantity */}
      <input
        type="number"
        min={1}
        value={item.quantity}
        onChange={(e) => onChange(index, 'quantity', Math.max(1, parseInt(e.target.value) || 1))}
        className="px-3 py-2 rounded-lg text-sm outline-none text-center"
        style={{
          background: 'var(--bg-input)',
          border: '1px solid var(--border-primary)',
          color: 'var(--text-primary)',
        }}
        onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--netrun-green)')}
        onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border-primary)')}
      />

      {/* Rate */}
      <div className="relative">
        <span
          className="absolute left-3 top-1/2 -translate-y-1/2 text-sm"
          style={{ color: 'var(--text-muted)' }}
        >
          $
        </span>
        <input
          type="text"
          value={rateInput}
          onChange={(e) => setRateInput(e.target.value)}
          onBlur={(e) => {
            handleRateBlur();
            e.currentTarget.style.borderColor = 'var(--border-primary)';
          }}
          placeholder="0.00"
          className="w-full pl-6 pr-3 py-2 rounded-lg text-sm outline-none"
          style={{
            background: 'var(--bg-input)',
            border: '1px solid var(--border-primary)',
            color: 'var(--text-primary)',
          }}
          onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--netrun-green)')}
        />
      </div>

      {/* Amount (calculated) */}
      <div
        className="px-3 py-2 rounded-lg text-sm text-right font-body"
        style={{ color: 'var(--text-primary)', background: 'var(--bg-hover)' }}
      >
        ${lineTotal.toFixed(2)}
      </div>

      {/* Remove */}
      {showRemove ? (
        <button
          onClick={() => onRemove(index)}
          className="flex items-center justify-center w-8 h-8 rounded-lg cursor-pointer transition-all"
          style={{ background: 'none', border: 'none', color: 'var(--text-muted)' }}
          title="Remove line item"
        >
          <Trash2 size={14} />
        </button>
      ) : (
        <div className="w-8" />
      )}
    </div>
  );
}

// ── Create Invoice Modal ──────────────────────────────────────────────

interface CreateModalProps {
  templates: InvoiceTemplate[];
  onClose: () => void;
  onCreated: () => void;
  prefillTemplate?: InvoiceTemplate | null;
}

function CreateInvoiceModal({ templates, onClose, onCreated, prefillTemplate }: CreateModalProps) {
  const [selectedTemplateId, setSelectedTemplateId] = useState(prefillTemplate?.id ?? '');
  const [customerEmail, setCustomerEmail] = useState('');
  const [lineItems, setLineItems] = useState<InvoiceLineItem[]>(
    prefillTemplate ? prefillTemplate.line_items.map((li) => ({ ...li })) : [emptyLineItem()]
  );
  const [dueDays, setDueDays] = useState(prefillTemplate?.default_due_days ?? 30);
  const [memo, setMemo] = useState(prefillTemplate?.default_memo ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Apply template when dropdown changes
  const applyTemplate = useCallback(
    (id: string) => {
      setSelectedTemplateId(id);
      const tpl = templates.find((t) => t.id === id);
      if (tpl) {
        setLineItems(tpl.line_items.map((li) => ({ ...li })));
        setDueDays(tpl.default_due_days);
        setMemo(tpl.default_memo);
      }
    },
    [templates]
  );

  const handleLineItemChange = (index: number, field: keyof InvoiceLineItem, value: string | number) => {
    setLineItems((prev) => prev.map((li, i) => (i === index ? { ...li, [field]: value } : li)));
  };

  const handleRemoveLineItem = (index: number) => {
    setLineItems((prev) => prev.filter((_, i) => i !== index));
  };

  const addLineItem = () => {
    setLineItems((prev) => [...prev, emptyLineItem()]);
  };

  const subtotal = lineItems.reduce((sum, li) => sum + (li.quantity * li.unit_amount) / 100, 0);

  const handleSave = async (andSend: boolean) => {
    setError('');
    if (lineItems.length === 0 || lineItems.some((li) => !li.description.trim())) {
      setError('All line items must have a description.');
      return;
    }
    setSaving(true);
    try {
      const res = await createInvoice({
        customer_email: customerEmail || undefined,
        line_items: lineItems,
        due_days: dueDays,
        memo: memo || undefined,
        template_id: selectedTemplateId || undefined,
      });
      if (andSend) {
        const invoiceId = res.data?.id;
        if (invoiceId) await sendInvoice(invoiceId);
      }
      onCreated();
      onClose();
    } catch (err) {
      console.error('Create invoice failed:', err);
      setError('Failed to create invoice. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.75)' }}
      onClick={onClose}
    >
      <div
        className="rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto slide-up"
        style={{
          background: '#141414',
          border: '1px solid var(--border-primary)',
          boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal header */}
        <div
          className="flex items-center justify-between px-6 py-4"
          style={{ borderBottom: '1px solid var(--border-primary)' }}
        >
          <h3 className="font-display text-base" style={{ color: 'var(--text-primary)' }}>
            Create Invoice
          </h3>
          <button
            onClick={onClose}
            className="cursor-pointer"
            style={{ background: 'none', border: 'none', color: 'var(--text-muted)' }}
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Error banner */}
          {error && (
            <div
              className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm"
              style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: 'var(--error)' }}
            >
              <AlertCircle size={14} />
              <span>{error}</span>
            </div>
          )}

          {/* Template selector */}
          {templates.length > 0 && (
            <div>
              <label className="block text-xs mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                Template (optional)
              </label>
              <select
                value={selectedTemplateId}
                onChange={(e) => applyTemplate(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg text-sm outline-none cursor-pointer"
                style={{
                  background: 'var(--bg-input)',
                  border: '1px solid var(--border-primary)',
                  color: selectedTemplateId ? 'var(--text-primary)' : 'var(--text-muted)',
                }}
              >
                <option value="">Select a template...</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Customer email */}
          <div>
            <label className="block text-xs mb-1.5" style={{ color: 'var(--text-secondary)' }}>
              Customer Email
            </label>
            <input
              type="email"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              placeholder="customer@example.com"
              className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
              style={{
                background: 'var(--bg-input)',
                border: '1px solid var(--border-primary)',
                color: 'var(--text-primary)',
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--netrun-green)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border-primary)')}
            />
          </div>

          {/* Line items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                Line Items
              </label>
              {/* Column headers */}
              <div
                className="grid gap-2 text-xs"
                style={{ gridTemplateColumns: '1fr 70px 110px 90px 32px', color: 'var(--text-muted)' }}
              >
                <span>Description</span>
                <span className="text-center">Qty</span>
                <span>Rate</span>
                <span className="text-right">Amount</span>
                <span />
              </div>
            </div>

            <div className="space-y-2">
              {lineItems.map((li, idx) => (
                <LineItemRow
                  key={idx}
                  item={li}
                  index={idx}
                  onChange={handleLineItemChange}
                  onRemove={handleRemoveLineItem}
                  showRemove={lineItems.length > 1}
                />
              ))}
            </div>

            <button
              onClick={addLineItem}
              className="flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-lg text-xs cursor-pointer transition-all"
              style={{ background: 'var(--bg-input)', border: '1px solid var(--border-subtle)', color: 'var(--text-secondary)' }}
            >
              <Plus size={12} />
              Add Line Item
            </button>
          </div>

          {/* Due days + Memo row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                Due In (days)
              </label>
              <input
                type="number"
                min={0}
                value={dueDays}
                onChange={(e) => setDueDays(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                style={{
                  background: 'var(--bg-input)',
                  border: '1px solid var(--border-primary)',
                  color: 'var(--text-primary)',
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--netrun-green)')}
                onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border-primary)')}
              />
            </div>
            <div>
              <label className="block text-xs mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                Memo
              </label>
              <input
                type="text"
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                placeholder="Optional note on invoice"
                className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                style={{
                  background: 'var(--bg-input)',
                  border: '1px solid var(--border-primary)',
                  color: 'var(--text-primary)',
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--netrun-green)')}
                onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border-primary)')}
              />
            </div>
          </div>

          {/* Total */}
          <div
            className="flex items-center justify-between px-4 py-3 rounded-lg"
            style={{ background: 'var(--bg-hover)', border: '1px solid var(--border-subtle)' }}
          >
            <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Total
            </span>
            <span className="font-display text-lg" style={{ color: 'var(--netrun-green)' }}>
              ${subtotal.toFixed(2)}
            </span>
          </div>

          {/* Footer actions */}
          <div
            className="flex items-center justify-end gap-3 pt-2"
            style={{ borderTop: '1px solid var(--border-primary)' }}
          >
            <button
              onClick={onClose}
              className="px-4 py-2.5 rounded-lg text-sm cursor-pointer transition-all"
              style={{
                background: 'var(--bg-input)',
                border: '1px solid var(--border-subtle)',
                color: 'var(--text-secondary)',
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => handleSave(false)}
              disabled={saving}
              className="px-4 py-2.5 rounded-lg text-sm cursor-pointer transition-all"
              style={{
                background: 'var(--bg-input)',
                border: '1px solid var(--border-primary)',
                color: 'var(--text-primary)',
                opacity: saving ? 0.5 : 1,
              }}
            >
              {saving ? <Loader size={14} className="animate-spin inline mr-1" /> : null}
              Save Draft
            </button>
            <button
              onClick={() => handleSave(true)}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm cursor-pointer transition-all"
              style={{
                background: 'var(--netrun-green)',
                border: 'none',
                color: 'var(--netrun-black)',
                opacity: saving ? 0.5 : 1,
              }}
            >
              {saving && <Loader size={14} className="animate-spin" />}
              <Send size={14} />
              Create & Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────

export default function BillingPage() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [templates, setTemplates] = useState<InvoiceTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [prefillTemplate, setPrefillTemplate] = useState<InvoiceTemplate | null>(null);
  const [actionLoading, setActionLoading] = useState<string>('');
  const [plans, setPlans] = useState<Record<string, { name: string; price: number; contacts: number | string; campaigns: number | string }>>({});
  const [checkoutLoading, setCheckoutLoading] = useState<string>('');
  const [usage, setUsage] = useState<{
    usage: Record<string, number>;
    plan: string;
    limits: Record<string, number>;
    contacts_pct: number;
    campaigns_pct: number;
  } | null>(null);

  // ── Mock data (used when API is unavailable) ─────────────────────

  const mockInvoices: Invoice[] = [
    {
      id: 'inv_001',
      invoice_number: 'INV-2026-001',
      status: 'paid',
      subtotal: 125000,
      tax: 0,
      total: 125000,
      amount_paid: 125000,
      amount_due: 0,
      currency: 'usd',
      invoice_date: '2026-03-01T00:00:00Z',
      due_date: '2026-03-15T00:00:00Z',
      paid_at: '2026-03-12T00:00:00Z',
      line_items: [{ description: 'IT Support – 10 hrs @ $125/hr', quantity: 10, unit_amount: 12500, currency: 'usd' }],
      invoice_pdf_url: null,
      hosted_invoice_url: null,
      created_at: '2026-03-01T00:00:00Z',
    },
    {
      id: 'inv_002',
      invoice_number: 'INV-2026-002',
      status: 'open',
      subtotal: 62500,
      tax: 0,
      total: 62500,
      amount_paid: 0,
      amount_due: 62500,
      currency: 'usd',
      invoice_date: '2026-03-10T00:00:00Z',
      due_date: '2026-03-25T00:00:00Z',
      paid_at: null,
      line_items: [{ description: 'IT Support – 5 hrs @ $125/hr', quantity: 5, unit_amount: 12500, currency: 'usd' }],
      invoice_pdf_url: null,
      hosted_invoice_url: null,
      created_at: '2026-03-10T00:00:00Z',
    },
    {
      id: 'inv_003',
      invoice_number: 'INV-2026-003',
      status: 'draft',
      subtotal: 25000,
      tax: 0,
      total: 25000,
      amount_paid: 0,
      amount_due: 25000,
      currency: 'usd',
      invoice_date: '2026-03-18T00:00:00Z',
      due_date: null,
      paid_at: null,
      line_items: [{ description: 'Network Audit – 2 hrs', quantity: 2, unit_amount: 12500, currency: 'usd' }],
      invoice_pdf_url: null,
      hosted_invoice_url: null,
      created_at: '2026-03-18T00:00:00Z',
    },
  ];

  const mockTemplates: InvoiceTemplate[] = [
    {
      id: 'tpl_ojai_hourly',
      name: 'Ojai IT Hourly',
      description: 'Standard hourly IT support at $125/hr',
      line_items: [{ description: 'IT Support', quantity: 1, unit_amount: 12500, currency: 'usd' }],
      default_due_days: 15,
      default_memo: 'Thank you for your business!',
      is_default: true,
    },
    {
      id: 'tpl_network_audit',
      name: 'Network Audit',
      description: 'On-site network assessment and report',
      line_items: [{ description: 'Network Audit', quantity: 1, unit_amount: 25000, currency: 'usd' }],
      default_due_days: 30,
      default_memo: 'Includes written assessment report.',
      is_default: false,
    },
    {
      id: 'tpl_monthly_msp',
      name: 'Monthly MSP Retainer',
      description: 'Fixed monthly managed services',
      line_items: [{ description: 'Managed IT Services – Monthly Retainer', quantity: 1, unit_amount: 150000, currency: 'usd' }],
      default_due_days: 5,
      default_memo: 'Recurring monthly managed services agreement.',
      is_default: false,
    },
  ];

  // ── Data loading ─────────────────────────────────────────────────

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [invRes, tplRes, plansRes, usageRes] = await Promise.allSettled([
        getInvoices(50),
        getInvoiceTemplates(),
        getBillingPlans(),
        getUsage(),
      ]);

      if (invRes.status === 'fulfilled') {
        const data = invRes.value.data;
        setInvoices(Array.isArray(data) ? data : data?.invoices ?? data?.items ?? []);
      } else {
        // API not yet implemented — fall back to mock
        setInvoices(mockInvoices);
      }

      if (tplRes.status === 'fulfilled') {
        const data = tplRes.value.data;
        setTemplates(Array.isArray(data) ? data : data?.templates ?? []);
      } else {
        setTemplates(mockTemplates);
      }

      if (plansRes.status === 'fulfilled') {
        const data = plansRes.value;
        setPlans(data?.plans ?? {});
      }

      if (usageRes.status === 'fulfilled') {
        setUsage(usageRes.value);
      }
    } catch {
      setInvoices(mockInvoices);
      setTemplates(mockTemplates);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Actions ──────────────────────────────────────────────────────

  const handleSend = async (invoice: Invoice) => {
    setActionLoading(invoice.id);
    try {
      await sendInvoice(invoice.id);
      await loadData();
    } catch {
      setError(`Failed to send invoice ${invoice.invoice_number}`);
    } finally {
      setActionLoading('');
    }
  };

  const handleVoid = async (invoice: Invoice) => {
    if (!window.confirm(`Void invoice ${invoice.invoice_number}? This cannot be undone.`)) return;
    setActionLoading(invoice.id);
    try {
      await voidInvoice(invoice.id);
      await loadData();
    } catch {
      setError(`Failed to void invoice ${invoice.invoice_number}`);
    } finally {
      setActionLoading('');
    }
  };

  const openCreateModal = (template?: InvoiceTemplate) => {
    setPrefillTemplate(template ?? null);
    setModalOpen(true);
  };

  const handleQuickCreate = async (template: InvoiceTemplate) => {
    // For Ojai IT Hourly and templates with only one hourly line item, open modal pre-filled
    openCreateModal(template);
  };

  const handleSubscribe = async (planId: string) => {
    setCheckoutLoading(planId);
    try {
      const result = await createCheckoutSession(planId);
      if (result.status === 'coming_soon') {
        setError(`${result.plan.name} plan: ${result.message}`);
      } else if (result.checkout_url) {
        window.location.href = result.checkout_url;
      }
    } catch {
      setError('Failed to start checkout. Please try again.');
    } finally {
      setCheckoutLoading('');
    }
  };

  // ── Render ───────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="page-enter">
        <Header title="Billing" subtitle="Invoices & templates" />
        <div className="flex items-center justify-center py-20">
          <Loader size={24} className="animate-spin" style={{ color: 'var(--netrun-green)' }} />
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header
        title="Billing"
        subtitle={`${invoices.length} invoice${invoices.length !== 1 ? 's' : ''}`}
      />

      <div className="p-4 md:p-8 space-y-6">
        {/* Error banner */}
        {error && (
          <div
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm"
            style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: 'var(--error)' }}
          >
            <AlertCircle size={16} />
            <span>{error}</span>
            <button
              onClick={() => setError('')}
              className="ml-auto cursor-pointer"
              style={{ background: 'none', border: 'none', color: 'var(--error)' }}
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* ── Usage Metering ────────────────────────────────────────── */}
        {usage && (
          <Panel title={`Usage — ${usage.plan.charAt(0).toUpperCase() + usage.plan.slice(1)} Plan`}>
            <div className="space-y-4">
              {/* Contacts */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Contacts</span>
                  <span className="text-sm font-display" style={{ color: 'var(--text-primary)' }}>
                    {usage.usage.contacts?.toLocaleString() ?? 0} / {usage.limits.contacts >= 999999 ? 'Unlimited' : usage.limits.contacts?.toLocaleString()}
                  </span>
                </div>
                <div className="w-full h-2 rounded-full" style={{ background: 'var(--bg-tertiary)' }}>
                  <div
                    className="h-2 rounded-full transition-all"
                    style={{
                      width: `${Math.min(usage.contacts_pct, 100)}%`,
                      background: usage.contacts_pct > 90 ? 'linear-gradient(90deg, #ef4444, #dc2626)' : usage.contacts_pct > 70 ? 'linear-gradient(90deg, #f59e0b, #fbbf24)' : 'linear-gradient(90deg, #90b9ab, #22c55e)',
                    }}
                  />
                </div>
              </div>
              {/* Campaigns */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Campaigns</span>
                  <span className="text-sm font-display" style={{ color: 'var(--text-primary)' }}>
                    {usage.usage.campaigns?.toLocaleString() ?? 0} / {usage.limits.campaigns >= 999999 ? 'Unlimited' : usage.limits.campaigns?.toLocaleString()}
                  </span>
                </div>
                <div className="w-full h-2 rounded-full" style={{ background: 'var(--bg-tertiary)' }}>
                  <div
                    className="h-2 rounded-full transition-all"
                    style={{
                      width: `${Math.min(usage.campaigns_pct, 100)}%`,
                      background: usage.campaigns_pct > 90 ? 'linear-gradient(90deg, #ef4444, #dc2626)' : usage.campaigns_pct > 70 ? 'linear-gradient(90deg, #f59e0b, #fbbf24)' : 'linear-gradient(90deg, #3b82f6, #90b9ab)',
                    }}
                  />
                </div>
              </div>
              {/* Summary row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2" style={{ borderTop: '1px solid var(--border-primary)' }}>
                {Object.entries(usage.usage).map(([key, val]) => (
                  <div key={key} className="text-center">
                    <div className="font-display text-lg" style={{ color: 'var(--netrun-green)' }}>{val.toLocaleString()}</div>
                    <div className="text-xs capitalize" style={{ color: 'var(--text-muted)' }}>{key}</div>
                  </div>
                ))}
              </div>
            </div>
          </Panel>
        )}

        {/* ── Subscription Plans ─────────────────────────────────────── */}
        {Object.keys(plans).length > 0 && (
          <div>
            <p className="text-xs mb-3 font-body" style={{ color: 'var(--text-muted)' }}>
              Subscription Plans
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(plans).map(([planId, plan]) => {
                const isPro = planId === 'professional';
                return (
                  <div
                    key={planId}
                    className="rounded-xl p-6 flex flex-col card-interactive"
                    style={{
                      background: isPro ? 'rgba(144,185,171,0.08)' : 'var(--bg-panel)',
                      border: isPro ? '2px solid rgba(144,185,171,0.5)' : '1px solid var(--border-primary)',
                      boxShadow: isPro ? '0 0 20px rgba(144,185,171,0.15)' : 'none',
                    }}
                  >
                    <h3 className="font-display text-base mb-1" style={{ color: 'var(--text-primary)' }}>
                      {plan.name}
                    </h3>
                    <div className="flex items-baseline gap-1 mb-4">
                      <span className="font-display text-2xl" style={{ color: 'var(--netrun-green)' }}>
                        ${plan.price}
                      </span>
                      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>/month</span>
                    </div>
                    <div className="space-y-2 mb-6 flex-1">
                      <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                        <Check size={14} style={{ color: 'var(--netrun-green)' }} />
                        <span>{typeof plan.contacts === 'number' ? plan.contacts.toLocaleString() : plan.contacts} contacts</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                        <Check size={14} style={{ color: 'var(--netrun-green)' }} />
                        <span>{typeof plan.campaigns === 'number' ? plan.campaigns : plan.campaigns} campaigns</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                        <Check size={14} style={{ color: 'var(--netrun-green)' }} />
                        <span>AI insights</span>
                      </div>
                      {planId === 'enterprise' && (
                        <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                          <Check size={14} style={{ color: 'var(--netrun-green)' }} />
                          <span>Priority support</span>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => handleSubscribe(planId)}
                      disabled={!!checkoutLoading}
                      className="w-full py-2.5 rounded-lg text-sm font-body cursor-pointer transition-all"
                      style={{
                        background: isPro ? 'var(--netrun-green)' : 'var(--bg-input)',
                        color: isPro ? 'var(--netrun-black)' : 'var(--text-primary)',
                        border: isPro ? 'none' : '1px solid var(--border-primary)',
                        opacity: checkoutLoading ? 0.5 : 1,
                      }}
                    >
                      {checkoutLoading === planId ? (
                        <Loader size={14} className="animate-spin inline" />
                      ) : (
                        'Subscribe'
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Toolbar */}
        <div className="flex items-center justify-end">
          <button
            onClick={() => openCreateModal()}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-body cursor-pointer transition-all"
            style={{ background: 'var(--netrun-green)', border: 'none', color: 'var(--netrun-black)' }}
          >
            <Plus size={16} />
            Create Invoice
          </button>
        </div>

        {/* ── Quick Invoice Templates ────────────────────────────────── */}
        {templates.length > 0 && (
          <div>
            <p className="text-xs mb-3 font-body" style={{ color: 'var(--text-muted)' }}>
              Quick Invoice
            </p>
            <div className="flex flex-wrap gap-3">
              {templates.map((tpl) => {
                const rate = tpl.line_items[0]?.unit_amount ?? 0;
                const isOjaiHourly = tpl.is_default;
                return (
                  <button
                    key={tpl.id}
                    onClick={() => handleQuickCreate(tpl)}
                    className="flex flex-col gap-1 px-5 py-4 rounded-xl text-left cursor-pointer transition-all duration-200"
                    style={{
                      background: isOjaiHourly ? 'rgba(144,185,171,0.12)' : 'var(--bg-panel)',
                      border: `1px solid ${isOjaiHourly ? 'rgba(144,185,171,0.4)' : 'var(--border-primary)'}`,
                      minWidth: '180px',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(144,185,171,0.5)';
                      e.currentTarget.style.background = 'rgba(144,185,171,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = isOjaiHourly ? 'rgba(144,185,171,0.4)' : 'var(--border-primary)';
                      e.currentTarget.style.background = isOjaiHourly ? 'rgba(144,185,171,0.12)' : 'var(--bg-panel)';
                    }}
                  >
                    <span className="text-sm font-body" style={{ color: 'var(--text-primary)' }}>
                      {tpl.name}
                    </span>
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      {formatAmount(rate, 'usd')}/unit
                    </span>
                    <span
                      className="flex items-center gap-1 text-xs mt-1"
                      style={{ color: 'var(--netrun-green)' }}
                    >
                      <Plus size={10} />
                      Create Invoice
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Invoice Table ──────────────────────────────────────────── */}
        <Panel noPadding>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-primary)' }}>
                  {['Invoice #', 'Date', 'Status', 'Amount', 'Due', 'Customer', 'Actions'].map((h) => (
                    <th
                      key={h}
                      className="text-left px-5 py-3 font-body text-xs"
                      style={{ color: 'var(--text-muted)' }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="text-center py-12 text-sm"
                      style={{ color: 'var(--text-muted)' }}
                    >
                      No invoices yet — click "Create Invoice" to get started
                    </td>
                  </tr>
                ) : (
                  invoices.map((invoice) => {
                    const cfg = statusConfig[invoice.status] ?? { label: invoice.status, variant: 'default' };
                    const isActing = actionLoading === invoice.id;
                    const canSend = invoice.status === 'draft' || invoice.status === 'open';
                    const canVoid = invoice.status === 'draft' || invoice.status === 'open';

                    // Extract customer from line items memo or use a dash
                    const customerDisplay = invoice.invoice_number;

                    return (
                      <tr
                        key={invoice.id}
                        className="transition-all duration-150"
                        style={{ borderBottom: '1px solid var(--border-primary)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-input)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        {/* Invoice number */}
                        <td className="px-5 py-3.5 font-body" style={{ color: 'var(--text-primary)' }}>
                          {customerDisplay}
                        </td>

                        {/* Date */}
                        <td className="px-5 py-3.5" style={{ color: 'var(--text-secondary)' }}>
                          {formatDate(invoice.invoice_date)}
                        </td>

                        {/* Status */}
                        <td className="px-5 py-3.5">
                          <Badge label={cfg.label} variant={cfg.variant as any} />
                        </td>

                        {/* Amount */}
                        <td className="px-5 py-3.5 font-body" style={{ color: 'var(--text-primary)' }}>
                          {formatAmount(invoice.total, invoice.currency)}
                        </td>

                        {/* Due date */}
                        <td className="px-5 py-3.5" style={{ color: 'var(--text-muted)' }}>
                          {formatDate(invoice.due_date)}
                        </td>

                        {/* Customer placeholder */}
                        <td className="px-5 py-3.5" style={{ color: 'var(--text-secondary)' }}>
                          {invoice.paid_at ? (
                            <span style={{ color: 'var(--success)' }}>Paid {formatDate(invoice.paid_at)}</span>
                          ) : (
                            <span style={{ color: 'var(--text-muted)' }}>—</span>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2">
                            {/* View PDF */}
                            {invoice.invoice_pdf_url && (
                              <a
                                href={invoice.invoice_pdf_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="View PDF"
                                className="p-1.5 rounded cursor-pointer"
                                style={{ color: 'var(--text-muted)' }}
                              >
                                <ExternalLink size={14} />
                              </a>
                            )}

                            {/* Send */}
                            {canSend && (
                              <button
                                onClick={() => handleSend(invoice)}
                                disabled={isActing}
                                title="Send invoice"
                                className="p-1.5 rounded cursor-pointer transition-all"
                                style={{ background: 'none', border: 'none', color: 'var(--text-muted)' }}
                              >
                                {isActing ? (
                                  <Loader size={14} className="animate-spin" />
                                ) : (
                                  <Send size={14} />
                                )}
                              </button>
                            )}

                            {/* Void */}
                            {canVoid && (
                              <button
                                onClick={() => handleVoid(invoice)}
                                disabled={isActing}
                                title="Void invoice"
                                className="p-1.5 rounded cursor-pointer transition-all"
                                style={{ background: 'none', border: 'none', color: 'var(--text-muted)' }}
                              >
                                {isActing ? (
                                  <Loader size={14} className="animate-spin" />
                                ) : (
                                  <Ban size={14} />
                                )}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>

      {/* Create Invoice Modal */}
      {modalOpen && (
        <CreateInvoiceModal
          templates={templates}
          onClose={() => { setModalOpen(false); setPrefillTemplate(null); }}
          onCreated={loadData}
          prefillTemplate={prefillTemplate}
        />
      )}
    </div>
  );
}
